import { useQuery } from '@tanstack/react-query';
import { fetchWithCache } from '../utils/apiUtils';
import { startMeasure, endMeasure } from '../utils/performanceMonitor';

// API endpoints
const API_ENDPOINTS = {
  projects: 'https://zpenyvg1n1.execute-api.us-west-2.amazonaws.com/prd/projects/',
  team: 'https://zpenyvg1n1.execute-api.us-west-2.amazonaws.com/prd/team/',
  general: 'https://zpenyvg1n1.execute-api.us-west-2.amazonaws.com/prd/general/',
  funfacts: 'https://zpenyvg1n1.execute-api.us-west-2.amazonaws.com/prd/funfacts/'
};

// Function to fetch project data from API Gateway
async function fetchProjectData() {
  try {
    startMeasure('fetch_project_data');
    const response = await fetchWithCache(API_ENDPOINTS.projects, {}, 300); // 5 minute cache
    
    if (!response || !response.body) {
      console.error('Failed to fetch project data or body is undefined');
      return [];
    }

    let rawData;
    try {
      rawData = JSON.parse(response.body);
    } catch (error) {
      console.error('Error parsing JSON from body:', error);
      return [];
    }

    if (!Array.isArray(rawData)) {
      console.error('Unexpected data structure:', rawData);
      return [];
    }

    // Transform the data to match ProjectData structure
    const transformedData = rawData.map(item => ({
      id: item.id,
      img: item.img,
      isFeature: item.isFeature,
      isInGallery: item.isInGallery,
      name: item.name,
      details: item.details,
      project_lead: item.project_lead,
      highlight: item.highlight,
      type: item.type,
      completion_year: item.completion_year,
      location: item.location,
      client_bio: item.client_bio,
      project_features_1: item.project_features_1,
      project_features_bullet_1: item.project_features_bullet_1,
      project_features_2: item.project_features_2,
      project_features_bullet_2: item.project_features_bullet_2,
      project_features_3: item.project_features_3,
      project_features_bullet_3: item.project_features_bullet_3,
      bgImg: item.bgImg,
      tag: item.tag || "workplace-design",
      detail_images: (() => {
        try {
          const parsedImages = item.detail_images;
          return Array.isArray(parsedImages) ? parsedImages.map((detail_images) => ({
            image: detail_images.image,
            id: detail_images.id
          })) : [];
        } catch (error) {
          console.error(`Error parsing detailImages for item ${item.id}:`, error);
          return [];
        }
      })()
    }));
    
    endMeasure('fetch_project_data');
    return transformedData;
  } catch (error) {
    console.error('Error fetching project data:', error);
    throw error;
  }
}

// Function to fetch team data from API Gateway
async function fetchTeamData() {
  try {
    startMeasure('fetch_team_data');
    const response = await fetchWithCache(API_ENDPOINTS.team, {}, 300); // 5 minute cache
    
    if (!response || !response.body) {
      console.error('Failed to fetch team data or body is undefined');
      return [];
    }

    let rawData;
    try {
      rawData = JSON.parse(response.body);
    } catch (error) {
      console.error('Error parsing JSON from body:', error);
      return [];
    }

    if (!Array.isArray(rawData)) {
      console.error('Unexpected data structure:', rawData);
      return [];
    }

    // Transform the data to match TeamData structure
    const transformedData = rawData.map(item => ({
      id: item.id,
      img: item.img,
      name: item.name,
      designation: item.designation,
      blurb: item.blurb,
    }));
    
    endMeasure('fetch_team_data');
    return transformedData;
  } catch (error) {
    console.error('Error fetching team data:', error);
    throw error;
  }
}

// Function to fetch general data from API Gateway
async function fetchGeneralData() {
  try {
    startMeasure('fetch_general_data');
    const response = await fetchWithCache(API_ENDPOINTS.general, {}, 300); // 5 minute cache
    
    if (!response || !response.body) {
      console.error('Failed to fetch general data or body is undefined');
      return [];
    }

    let rawData;
    try {
      rawData = JSON.parse(response.body);
    } catch (error) {
      console.error('Error parsing JSON from body:', error);
      return [];
    }

    if (!Array.isArray(rawData)) {
      console.error('Unexpected data structure:', rawData);
      return [];
    }

    // Transform the data to match GeneralData structure
    const transformedData = rawData.map(item => ({
      id: item.id,
      description_text_small: item.description_text_small,
      mission_statement_text: item.mission_statement_text,
      location_address_text: item.location_address_text,
      location_suite: item.location_suite,
      location_city_state_zip_text: item.location_city_state_zip_text,
      location_phone_text: item.location_phone_text,
      location_email_text: item.location_email_text,
      location_fax_text: item.location_fax_text,
      about_header: item.about_header,
      about_mission: item.about_mission,
      about_us_1_header: item.about_us_1_header,
      about_us_1_text: item.about_us_1_text,
      about_us_2_header: item.about_us_2_header,
      about_us_2_text: item.about_us_2_text,
      about_us_3_header: item.about_us_3_header,
      about_us_3_text: item.about_us_3_text,
      about_us_4_header: item.about_us_4_header,
      about_us_4_text: item.about_us_4_text,
      contact_message: item.contact_message,
      instagram_url: item.instagram_url,
      facebook_url: item.facebook_url,
      x_url: item.x_url,
      T_N_C_P: item.T_N_C_P,
      T_N_C_URL: item.T_N_C_URL,
      evd_copyright: item.evd_copyright
    }));
    
    endMeasure('fetch_general_data');
    return transformedData;
  } catch (error) {
    console.error('Error fetching general data:', error);
    throw error;
  }
}

// React Query hooks
export function useProjectDataQuery() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjectData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    retry: 3,
    onError: (error) => console.error('Error fetching project data:', error)
  });
}

export function useTeamDataQuery() {
  return useQuery({
    queryKey: ['team'],
    queryFn: fetchTeamData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    retry: 3,
    onError: (error) => console.error('Error fetching team data:', error)
  });
}

export function useGeneralDataQuery() {
  return useQuery({
    queryKey: ['general'],
    queryFn: fetchGeneralData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    retry: 3,
    onError: (error) => console.error('Error fetching general data:', error)
  });
}

// Function to fetch fun facts data from API Gateway
async function fetchFunFactsData() {
  try {
    startMeasure('fetch_funfacts_data');
    const response = await fetchWithCache(API_ENDPOINTS.funfacts, {}, 300); // 5 minute cache
    
    if (!response || !response.body) {
      console.error('Failed to fetch fun facts data or body is undefined');
      return [];
    }

    let rawData;
    try {
      rawData = JSON.parse(response.body);
    } catch (error) {
      console.error('Error parsing JSON from body:', error);
      return [];
    }

    if (!Array.isArray(rawData)) {
      console.error('Unexpected data structure:', rawData);
      return [];
    }

    // Transform the data to match FunFactData structure
    const transformedData = rawData.map(item => ({
      id: item.id,
      contentName: item.contentName,
      contentNumber: item.contentNumber,
      plus: item.plus || '',
      img: item.img
    }));
    
    endMeasure('fetch_funfacts_data');
    return transformedData;
  } catch (error) {
    console.error('Error fetching fun facts data:', error);
    throw error;
  }
}

export function useFunFactsDataQuery() {
  return useQuery({
    queryKey: ['funfacts'],
    queryFn: fetchFunFactsData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    retry: 3,
    onError: (error) => console.error('Error fetching fun facts data:', error)
  });
}
